import { useState } from "react";
import { KeyRound, Save, UserCog } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ADMIN_NAV } from "./nav";
import { useAuth } from "@/context/AuthContext";
import { userService } from "@/services/userService";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const card = "rounded-md border border-[#22252F] bg-[#12141A] p-6";
const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#94A3B8]";

export default function AdminSettingsPage() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await userService.updateProfile({ name: profile.name, email: profile.email });
      await refresh();
      toast.success("Dados de acesso atualizados");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("As palavras-passe novas não coincidem");
      return;
    }
    setBusy(true);
    try {
      await userService.changePassword(pwd.current_password, pwd.new_password);
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Palavra-passe alterada");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <AppShell nav={ADMIN_NAV} title="Minha conta" subtitle="Email de login e palavra-passe do administrador">
      <div data-testid="admin-settings-page" className="max-w-xl space-y-6">
        <form onSubmit={saveProfile} className={`${card} space-y-4`}>
          <p className="flex items-center gap-2 text-sm text-white"><UserCog className="h-4 w-4" /> Dados de acesso</p>
          <div className="space-y-2">
            <Label className={labelCls}>Nome</Label>
            <Input data-testid="admin-name-input" value={profile.name}
                   onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={field} />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Email de login</Label>
            <Input data-testid="admin-email-input" type="email" value={profile.email}
                   onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={field} />
          </div>
          <Button type="submit" disabled={busy} data-testid="save-admin-profile"
                  className="h-11 bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Save className="mr-2 h-4 w-4" /> Guardar
          </Button>
        </form>

        <form onSubmit={savePassword} className={`${card} space-y-4`}>
          <p className="flex items-center gap-2 text-sm text-white"><KeyRound className="h-4 w-4" /> Alterar palavra-passe</p>
          <div className="space-y-2">
            <Label className={labelCls}>Palavra-passe atual</Label>
            <Input data-testid="admin-current-password" type="password" value={pwd.current_password}
                   onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className={field} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className={labelCls}>Nova palavra-passe</Label>
              <Input data-testid="admin-new-password" type="password" minLength={6} value={pwd.new_password}
                     onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className={field} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Confirmar</Label>
              <Input data-testid="admin-confirm-password" type="password" minLength={6} value={pwd.confirm}
                     onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={field} />
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="save-admin-password"
                  className="h-11 bg-[#2563EB] hover:bg-[#1D4ED8]">
            Alterar palavra-passe
          </Button>
        </form>
      </div>
    </AppShell>
  );
}
